"use client"

import React, { useState } from "react"
import { ThumbsUp, MessageSquare, Share2, Send, Heart, Lightbulb, Clapperboard, RefreshCw, Repeat2, Globe, MoreHorizontal } from "lucide-react";
import { cn } from "../../lib/utils"

export function LinkedInPostCard({
  name,
  avatar,
  headline,
  timeAgo,
  content,
  postImage,
  reactions,
  comments,
  reposts,
  isFollowing = false,
  className,
}) {
  const [hasReacted, setHasReacted] = useState(false)
  const [reactionsCount, setReactionsCount] = useState(reactions)
  const [showReactions, setShowReactions] = useState(false)
  const [selectedReaction, setSelectedReaction] = useState(null)

  const reactionIcons = [
    { name: "like", emoji: "👍", color: "#0A66C2" },
    { name: "celebrate", emoji: "👏", color: "#44712E" },
    { name: "support", emoji: "❤️", color: "#B24020" },
    { name: "insightful", emoji: "💡", color: "#915907" },
    { name: "curious", emoji: "🤔", color: "#6B3FA0" },
  ]

  const handleReaction = (reactionName) => {
    if (hasReacted && !reactionName) {
      setHasReacted(false)
      setSelectedReaction(null)
      setReactionsCount(reactionsCount - 1)
    } else {
      setHasReacted(true)
      setSelectedReaction(reactionName || "like")
      if (!hasReacted) {
        setReactionsCount(reactionsCount + 1)
      }
    }
    setShowReactions(false)
  }

  const formatCount = (count) => {
    if (count >= 1000000) {
      return `${(count / 1000000).toFixed(1)}M`
    }
    if (count >= 1000) {
      return `${(count / 1000).toFixed(0)}K`
    }
    return count.toLocaleString()
  }

  const getSelectedReaction = () => {
    return reactionIcons.find((r) => r.name === selectedReaction) || reactionIcons[0]
  }

  return (
    <div
      className={cn(
        "w-full max-w-[550px] bg-card border border-border rounded-lg overflow-hidden shadow-sm",
        className
      )}
    >
      {/* Header */}
      <div className="flex items-start justify-between px-4 pt-3 pb-2">
        <div className="flex gap-2">
          <div className="relative w-12 h-12 rounded-full overflow-hidden flex-shrink-0">
            <img
              src={avatar || "/placeholder.svg"}
              alt={name}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="flex flex-col text-left">
            <div className="flex items-center gap-1.5 text-left">
              <span className="text-sm font-semibold text-foreground hover:text-[#0A66C2] hover:underline cursor-pointer">
                {name}
              </span>
              {!isFollowing && (
                <>
                  <span className="text-muted-foreground">•</span>
                  <button className="text-sm font-semibold text-[#0A66C2] hover:underline">
                    Seguir
                  </button>
                </>
              )}
            </div>
            <span className="text-xs text-muted-foreground line-clamp-1 text-left">
              {headline}
            </span>
            <div className="flex items-center gap-1 text-xs text-muted-foreground text-left">
              <span>{timeAgo}</span>
              <span>•</span>
              <Globe className="w-3 h-3" />
            </div>
          </div>
        </div>
        <button className="p-2 hover:bg-muted rounded-full transition-colors">
          <MoreHorizontal className="w-5 h-5 text-muted-foreground" />
        </button>
      </div>

      {/* Content */}
      <div className="px-4 pb-3 text-left">
        <p className="text-sm text-foreground whitespace-pre-line">{content}</p>
      </div>

      {/* Post Image */}
      {postImage && (
        <div className="relative w-full border-t border-border bg-muted/30">
          <img
            src={postImage || "/placeholder.svg"}
            alt="Post content"
            className="w-full h-auto block"
          />
        </div>
      )}

      {/* Engagement Stats */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-border">
        <div className="flex items-center gap-1">
          <div className="flex -space-x-1">
            <span className="w-4 h-4 rounded-full bg-[#0A66C2] flex items-center justify-center text-[8px]">
              👍
            </span>
            <span className="w-4 h-4 rounded-full bg-[#B24020] flex items-center justify-center text-[8px]">
              ❤️
            </span>
            <span className="w-4 h-4 rounded-full bg-[#44712E] flex items-center justify-center text-[8px]">
              👏
            </span>
          </div>
          <span className="text-xs text-muted-foreground hover:text-[#0A66C2] hover:underline cursor-pointer ml-1">
            {formatCount(reactionsCount)}
          </span>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          {comments > 0 && (
            <span className="hover:text-[#0A66C2] hover:underline cursor-pointer">
              {formatCount(comments)} comentarios
            </span>
          )}
          {reposts > 0 && (
            <>
              <span>•</span>
              <span className="hover:text-[#0A66C2] hover:underline cursor-pointer">
                {formatCount(reposts)} publicaciones compartidas
              </span>
            </>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-around px-2 py-1">
        <div className="relative">
          <button
            className={cn(
              "flex items-center gap-1.5 px-3 py-3 rounded-lg hover:bg-muted transition-colors flex-1",
              hasReacted && "text-[#0A66C2]"
            )}
            onClick={() => handleReaction()}
            onMouseEnter={() => setShowReactions(true)}
            onMouseLeave={() => setShowReactions(false)}
          >
            {hasReacted ? (
              <span className="text-base">{getSelectedReaction().emoji}</span>
            ) : (
              <ThumbsUp className="w-5 h-5 text-muted-foreground" />
            )}
            <span
              className={cn(
                "text-xs font-semibold",
                hasReacted ? "text-[#0A66C2]" : "text-muted-foreground"
              )}
            >
              {hasReacted ? getSelectedReaction().name === "like" ? "Me gusta" : 
                getSelectedReaction().name === "celebrate" ? "Celebrar" :
                getSelectedReaction().name === "support" ? "Encantar" :
                getSelectedReaction().name === "insightful" ? "Interesante" : "Me interesa"
              : "Recomendar"}
            </span>
          </button>

          {/* Reactions Popup */}
          {showReactions && (
            <div
              className="absolute bottom-full left-0 mb-1 flex items-center gap-1 bg-card border border-border rounded-full px-2 py-1.5 shadow-lg"
              onMouseEnter={() => setShowReactions(true)}
              onMouseLeave={() => setShowReactions(false)}
            >
              {reactionIcons.map((reaction) => (
                <button
                  key={reaction.name}
                  className="text-2xl hover:scale-125 transition-transform p-1"
                  onClick={() => handleReaction(reaction.name)}
                >
                  {reaction.emoji}
                </button>
              ))}
            </div>
          )}
        </div>

        <button className="flex items-center gap-1.5 px-3 py-3 rounded-lg hover:bg-muted transition-colors flex-1 justify-center">
          <MessageSquare className="w-5 h-5 text-muted-foreground" />
          <span className="text-xs font-semibold text-muted-foreground">Comentar</span>
        </button>

        <button className="flex items-center gap-1.5 px-3 py-3 rounded-lg hover:bg-muted transition-colors flex-1 justify-center">
          <Repeat2 className="w-5 h-5 text-muted-foreground" />
          <span className="text-xs font-semibold text-muted-foreground">Compartir</span>
        </button>

        <button className="flex items-center gap-1.5 px-3 py-3 rounded-lg hover:bg-muted transition-colors flex-1 justify-center">
          <Send className="w-5 h-5 text-muted-foreground" />
          <span className="text-xs font-semibold text-muted-foreground">Enviar</span>
        </button>
      </div>
    </div>
  );
}
